import React from "react";
import { Card } from "./ui/card";
import { GraduationCap, Heart, Home, Users } from "lucide-react";
import { aboutStory } from "../data/mockData";

const AboutSection = () => {
  return (
    <section id="about" className="relative py-20 overflow-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url("https://images.pexels.com/photos/6173699/pexels-photo-6173699.jpeg?auto=compress&cs=tinysrgb&w=2000")',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/80"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/40 to-gray-900/60"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Story Content */}
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl font-bold text-white mb-4">
                Our Story: From Corporate Layoffs to High Aspirations in Real Estate!
              </h2>
              <p className="text-xl text-gray-200 leading-relaxed">
                Founded by a family man from Detroit who turned personal challenges into a mission 
                to help homeowners across America.
              </p>
            </div>

            {/* Founder Story */}
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-600/80 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0">
                  <GraduationCap className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white mb-2">The Beginning</h3>
                  <p className="text-gray-200">
                    {aboutStory.founder} grew up in the heart of Detroit, Michigan, and graduated from 
                    {" " + aboutStory.education}. Like many graduates, he pursued the corporate dream 
                    with a successful career in sales at Dell.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-600/80 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0">
                  <div className="w-6 h-6 bg-red-200 rounded"></div>
                </div>
                <div>
                  <h3 className="font-bold text-white mb-2">The Challenge</h3>
                  <p className="text-gray-200">
                    {aboutStory.motivation} These experiences taught him the importance of financial 
                    security and alternative income streams.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-600/80 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0">
                  <Home className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white mb-2">The Discovery</h3>
                  <p className="text-gray-200">
                    Real estate became more than just an investment opportunity—it became a way to help 
                    families facing similar challenges. Winston discovered his true calling in helping 
                    people navigate difficult property situations.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-purple-600/80 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0">
                  <Heart className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white mb-2">The Family Support</h3>
                  <p className="text-gray-200">
                    {aboutStory.family}. Together, they built PropertySellEasy as a family business 
                    dedicated to providing homeowners with dignity, respect, and fair treatment.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Image and Stats */}
          <div className="space-y-6">
            {/* Family Photo */}
            <Card className="p-8 bg-black/60 backdrop-blur-sm border-gray-600 shadow-2xl">
              <div className="aspect-square rounded-lg overflow-hidden mb-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_property-solutions-1/artifacts/m42xovz3_family%20pic.jpg"
                  alt="The PropertySellEasy Family"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="text-center">
                <p className="text-white font-medium mb-2">The PropertySellEasy Family</p>
                <p className="text-sm text-gray-300">Winston, his wife, and their 2-year-old son</p>
              </div>
            </Card>

            {/* Company Stats */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="p-6 text-center bg-black/60 backdrop-blur-sm border-gray-600">
                <div className="text-3xl font-bold text-blue-400 mb-2">2019</div>
                <div className="text-sm text-gray-300">Founded</div>
              </Card>
              <Card className="p-6 text-center bg-black/60 backdrop-blur-sm border-gray-600">
                <div className="text-3xl font-bold text-green-400 mb-2">50</div>
                <div className="text-sm text-gray-300">States Served</div>
              </Card>
              <Card className="p-6 text-center bg-black/60 backdrop-blur-sm border-gray-600">
                <div className="text-3xl font-bold text-blue-400 mb-2">18</div>
                <div className="text-sm text-gray-300">Avg Days to Close</div>
              </Card>
              <Card className="p-6 text-center bg-black/60 backdrop-blur-sm border-gray-600">
                <div className="text-3xl font-bold text-green-400 mb-2">4.9★</div>
                <div className="text-sm text-gray-300">Customer Rating</div>
              </Card>
            </div>

            {/* Mission Statement */}
            <Card className="p-6 bg-blue-600/80 backdrop-blur-sm border-blue-500 text-white">
              <h3 className="text-xl font-bold mb-4">Our Mission</h3>
              <p className="leading-relaxed">
                {aboutStory.mission} We believe every homeowner deserves respect, transparency, 
                and a fair deal, regardless of their circumstances.
              </p>
            </Card>
          </div>
        </div>

        {/* Values Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-center text-white mb-12">Our Core Values</h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600/80 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-8 w-8 text-white" />
              </div>
              <h4 className="text-xl font-bold text-white mb-3">Family First</h4>
              <p className="text-gray-200">
                We understand that selling a home affects the whole family. Our approach is 
                compassionate and family-centered.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-600/80 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 border-4 border-white rounded"></div>
              </div>
              <h4 className="text-xl font-bold text-white mb-3">Transparency</h4>
              <p className="text-gray-200">
                No hidden fees, no surprise costs. We explain every step of the process 
                and provide clear, honest communication.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600/80 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                <Home className="h-8 w-8 text-white" />
              </div>
              <h4 className="text-xl font-bold text-white mb-3">Fair Dealing</h4>
              <p className="text-gray-200">
                Our AI-powered offers ensure you receive fair market value for your property, 
                backed by extensive market analysis.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;